import React, { useState } from 'react';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Paper,
  Link,
  CircularProgress
} from '@mui/material';
import { signIn, signOut, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loginSuccess, logout } from '../../slices/authSlice';

function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [email, setEmail] = useState(location.state?.email || '');
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [infoMsg, setInfoMsg] = useState(location.state?.message || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMsg('');

    try {
      // First check if there's a current user
      try {
        const currentUser = await getCurrentUser();
        // If the current user is the same one trying to log in
        if (currentUser.signInDetails?.loginId === email) {
          // Just get their session and proceed to dashboard
          const session = await fetchAuthSession();
          dispatch(
            loginSuccess({
              user: {
                username: currentUser.username,
                email: currentUser.signInDetails?.loginId,
              },
              token: session.tokens?.idToken?.toString() || ''
            })
          );
          navigate(location.state?.redirectAfterLogin || '/dashboard');
          return;
        } else {
          // Different user, sign out current user first
          await signOut();
          dispatch(logout());
        }
      } catch (error) {
        // No current user, proceed with sign in
      }

      // Sign in the new user
      await signIn({ username: email, password });
  
      // Get the authenticated user info
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      
      dispatch(
        loginSuccess({
          user: {
            username: user.username,
            email: user.signInDetails?.loginId || email,
          },
          token: session.tokens?.idToken?.toString() || ''
        })
      );

      navigate(location.state?.redirectAfterLogin || '/dashboard');
    } catch (error) {
      setErrorMsg(error.message || 'Error signing in');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Rest of the component remains the same...
  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Login
          </Typography>

          {errorMsg && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errorMsg}
            </Alert>
          )}

          {infoMsg && (
            <Alert severity="info" sx={{ mb: 3 }}>
              {infoMsg}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus={!email}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isSubmitting}
              autoFocus={!!email}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isSubmitting}
              sx={{ mt: 3, mb: 2 }}
            >
              {isSubmitting ? (
                <CircularProgress size={24} />
              ) : (
                'Sign In'
              )}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2">
                Don't have an account?{' '}
                <Link component={RouterLink} to="/signup">
                  Sign Up
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}

export default Login;